"use client";

import { useMemo } from "react";
import { Accordion as UIAccordion } from "@/components/ui/Accordion";
import { Typography } from "@/components/ui/Typography";

interface AccordionItemData {
	title: string;
	content?: string | null;
}

export interface AccordionProps {
	items: AccordionItemData[];
}

/**
 * Renders plain text content, converting `•` bullet lines into `<ul>/<li>` elements
 * and separating remaining text into paragraphs.
 */
const RichContent = ({ text }: { text: string }) => {
	const blocks: Array<{ type: "paragraph"; text: string } | { type: "list"; items: string[] }> = [];

	for (const raw of text.split(/\r?\n/)) {
		const line = raw.trim();
		if (!line) {
			continue;
		}

		if (line.startsWith("•")) {
			const item = line.replace(/^•\s*/, "");
			const last = blocks.at(-1);
			if (last?.type === "list") {
				last.items.push(item);
			} else {
				blocks.push({ type: "list", items: [item] });
			}
		} else {
			const last = blocks.at(-1);
			if (last?.type === "paragraph") {
				last.text += ` ${line}`;
			} else {
				blocks.push({ type: "paragraph", text: line });
			}
		}
	}

	return (
		<div className="flex flex-col gap-grid-component-spacing-2xs">
			{blocks.map((block, i) =>
				block.type === "list" ? (
					<ul key={i} className="list-disc pl-4">
						{block.items.map((item) => (
							<li key={item}>
								<Typography variant="body-sm-reg" as="span" className="text-text-dark-text-1">
									{item}
								</Typography>
							</li>
						))}
					</ul>
				) : (
					<Typography key={i} variant="body-sm-reg" className="text-text-dark-text-1">
						{block.text}
					</Typography>
				),
			)}
		</div>
	);
};

export const Accordion = ({ items }: AccordionProps) => {
	const accordionItems = useMemo(
		() =>
			items.map((item) => ({
				title: item.title,
				content: item.content ? <RichContent text={item.content} /> : undefined,
			})),
		[items],
	);

	return <UIAccordion items={accordionItems} size="large" />;
};
